import React, { useState, useEffect, useCallback } from 'react';
import { adminAPI } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import {
  ChevronLeft, ChevronRight, Plus, MapPin, Zap, UserPlus, X, Ban, AlertTriangle, Trash2, Clock, Calendar
} from 'lucide-react';
import {
  format, addMonths, subMonths, startOfMonth, endOfMonth,
  eachDayOfInterval, getDay, isSameDay, addDays, subDays,
  startOfWeek, endOfWeek, addWeeks, subWeeks, isToday as dfIsToday,
  differenceInHours, parse, isBefore
} from 'date-fns';
import { fr, enUS } from 'date-fns/locale';
import { toast } from 'sonner';
import { BookingDetailModal } from '@/components/admin/BookingDetailModal';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const statusColors = {
  confirmed: 'bg-[#00FF00]/15 text-[#00FF00]',
  pending: 'bg-yellow-500/15 text-yellow-400',
  pending_onsite: 'bg-blue-500/15 text-blue-400',
  pending_payment_link: 'bg-purple-500/15 text-purple-400',
  cancelled: 'bg-red-500/15 text-red-400',
};

const TIME_SLOTS = [];
for (let h = 7; h <= 20; h++) {
  TIME_SLOTS.push(`${String(h).padStart(2, '0')}:00`);
  TIME_SLOTS.push(`${String(h).padStart(2, '0')}:30`);
}

function isWithin48h(dateStr, timeStr) {
  try {
    const slotDate = parse(`${dateStr} ${timeStr}`, 'yyyy-MM-dd HH:mm', new Date());
    const now = new Date();
    if (isBefore(slotDate, now)) return false;
    return differenceInHours(slotDate, now) <= 48;
  } catch { return false; }
}

export default function AdminCalendar({ lang, onAddBooking, sites = [], user, onFlashPromo, activities = [] }) {
  const locale = lang === 'fr' ? fr : enUS;
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 640;
  const [view, setView] = useState(isMobile ? 'day' : 'week');
  const [currentDate, setCurrentDate] = useState(new Date());
  const [calData, setCalData] = useState([]);
  const [selectedDay, setSelectedDay] = useState(null);
  const isSuperAdmin = user?.role === 'super_admin';
  const [siteFilter, setSiteFilter] = useState(isSuperAdmin ? 'all' : (user?.site_id || 'all'));
  const [remainingSlots, setRemainingSlots] = useState([]);
  const [expandedPromo, setExpandedPromo] = useState(null);
  // Blocking state
  const [blockedSlots, setBlockedSlots] = useState([]);
  const [blockModal, setBlockModal] = useState(false);
  const [blockDate, setBlockDate] = useState('');
  const [blockMode, setBlockMode] = useState('day'); // 'day' or 'slots'
  const [blockTimeSlots, setBlockTimeSlots] = useState([]);
  const [blockActivityId, setBlockActivityId] = useState('');
  const [blockReason, setBlockReason] = useState('');
  const [blockSaving, setBlockSaving] = useState(false);
  const [blockConflicts, setBlockConflicts] = useState([]);
  const [selectedBookingId, setSelectedBookingId] = useState(null);

  const effectiveSiteId = isSuperAdmin ? (siteFilter !== 'all' ? siteFilter : null) : (user?.site_id || null);

  const getDateRange = useCallback(() => {
    if (view === 'month') return { start: startOfMonth(currentDate), end: endOfMonth(currentDate) };
    if (view === 'week') {
      return { start: startOfWeek(currentDate, { weekStartsOn: 1 }), end: endOfWeek(currentDate, { weekStartsOn: 1 }) };
    }
    if (view === '3day') return { start: currentDate, end: addDays(currentDate, 2) };
    return { start: currentDate, end: currentDate };
  }, [view, currentDate]);

  const loadCalendar = useCallback(async () => {
    const { start, end } = getDateRange();
    try {
      const res = await adminAPI.getCalendar(format(start, 'yyyy-MM-dd'), format(end, 'yyyy-MM-dd'), effectiveSiteId);
      setCalData(res.data);
    } catch { setCalData([]); }
  }, [getDateRange, effectiveSiteId]);

  const loadRemainingSlots = useCallback(async () => {
    if (view === 'month') { setRemainingSlots([]); return; }
    const siteId = effectiveSiteId;
    if (!siteId) {
      if (isSuperAdmin && sites.length > 0) {
        const all = [];
        for (const s of sites) {
          try {
            const r = await adminAPI.getRemainingSlots(s.id);
            all.push(...r.data.map(d => ({ ...d, site_id: s.id, site_name: s.name })));
          } catch {}
        }
        setRemainingSlots(all);
      } else { setRemainingSlots([]); }
      return;
    }
    try {
      const site = sites.find(s => s.id === siteId);
      const r = await adminAPI.getRemainingSlots(siteId);
      setRemainingSlots(r.data.map(d => ({ ...d, site_id: siteId, site_name: site?.name || '' })));
    } catch { setRemainingSlots([]); }
  }, [view, effectiveSiteId, isSuperAdmin, sites]);

  useEffect(() => { loadCalendar(); }, [loadCalendar]);
  useEffect(() => { loadRemainingSlots(); }, [loadRemainingSlots]);

  // Load blocked slots
  const loadBlockedSlots = useCallback(async () => {
    try {
      const res = await adminAPI.getBlockedSlots(effectiveSiteId);
      setBlockedSlots(res.data);
    } catch { setBlockedSlots([]); }
  }, [effectiveSiteId]);
  useEffect(() => { loadBlockedSlots(); }, [loadBlockedSlots]);

  const isSlotBlocked = (dateStr, time, activityId) => {
    return blockedSlots.some(b => {
      if (b.date !== dateStr) return false;
      if (b.activity_id && activityId && b.activity_id !== activityId) return false;
      const ts = b.time_slots || [];
      if (ts.length === 0) return true; // whole day
      return ts.includes(time);
    });
  };

  const isDayBlocked = (dateStr) => {
    return blockedSlots.some(b => b.date === dateStr && (!b.time_slots || b.time_slots.length === 0) && !b.activity_id);
  };

  const getBlocksForDay = (dateStr) => blockedSlots.filter(b => b.date === dateStr);

  const openBlockModal = (date) => {
    const dateStr = date ? format(date, 'yyyy-MM-dd') : format(currentDate, 'yyyy-MM-dd');
    setBlockDate(dateStr);
    setBlockMode('day');
    setBlockTimeSlots([]);
    setBlockActivityId('');
    setBlockReason('');
    setBlockConflicts([]);
    // Check for existing bookings on this date
    const dayData = calData.find(c => c.date === dateStr);
    setBlockConflicts(dayData?.bookings || []);
    setBlockModal(true);
  };

  const toggleBlockSlot = (slot) => {
    setBlockTimeSlots(prev => prev.includes(slot) ? prev.filter(s => s !== slot) : [...prev, slot]);
  };

  const handleBlock = async () => {
    if (!effectiveSiteId && !isSuperAdmin) { toast.error('Sélectionnez un site'); return; }
    const siteId = effectiveSiteId || (sites.length > 0 ? sites[0].id : null);
    if (!siteId) { toast.error('Aucun site disponible'); return; }
    setBlockSaving(true);
    try {
      await adminAPI.createBlockedSlot({
        site_id: siteId, date: blockDate,
        activity_id: blockActivityId || null,
        time_slots: blockMode === 'day' ? [] : blockTimeSlots,
        reason: blockReason,
      });
      toast.success('Créneau(x) bloqué(s)');
      setBlockModal(false);
      loadBlockedSlots();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
    setBlockSaving(false);
  };

  const handleUnblock = async (blockId) => {
    try {
      await adminAPI.deleteBlockedSlot(blockId);
      toast.success('Blocage supprimé');
      loadBlockedSlots();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
  };

  const getBookingsForDay = (d) => {
    const ds = format(d, 'yyyy-MM-dd');
    return calData.find(c => c.date === ds);
  };

  // Get active promos for a specific day+time
  const getActivePromos = (dateStr, time) => {
    const dayData = calData.find(c => c.date === dateStr);
    return (dayData?.promos || []).filter(p => p.time_slot === time);
  };

  // Get promo-eligible activities for a slot: only activities that ALREADY have bookings
  // AND still have remaining capacity (to optimize instructor sessions)
  const getPromoForSlot = (dateStr, time, bookedActivityIds) => {
    if (!isWithin48h(dateStr, time) || !bookedActivityIds || bookedActivityIds.length === 0) return [];
    const matches = remainingSlots.filter(rs =>
      rs.date === dateStr &&
      bookedActivityIds.includes(rs.activity_id) &&
      rs.slots.some(s => s.time_slot === time)
    );
    const seen = new Set();
    const deduped = [];
    for (const rs of matches) {
      if (seen.has(rs.activity_id)) continue;
      seen.add(rs.activity_id);
      deduped.push({ ...rs, slot: rs.slots.find(s => s.time_slot === time) });
    }
    return deduped;
  };

  const navigate = (dir) => {
    if (view === 'month') setCurrentDate(dir > 0 ? addMonths(currentDate, 1) : subMonths(currentDate, 1));
    else if (view === 'week') setCurrentDate(dir > 0 ? addWeeks(currentDate, 1) : subWeeks(currentDate, 1));
    else if (view === '3day') setCurrentDate(dir > 0 ? addDays(currentDate, 3) : subDays(currentDate, 3));
    else setCurrentDate(dir > 0 ? addDays(currentDate, 1) : subDays(currentDate, 1));
    setSelectedDay(null);
    setExpandedPromo(null);
  };

  const goToToday = () => { setCurrentDate(new Date()); setSelectedDay(null); setExpandedPromo(null); };

  const getTitle = () => {
    if (view === 'month') return format(currentDate, 'MMMM yyyy', { locale });
    if (view === 'week') {
      const s = startOfWeek(currentDate, { weekStartsOn: 1 });
      const e = endOfWeek(currentDate, { weekStartsOn: 1 });
      return `${format(s, 'd MMM', { locale })} - ${format(e, 'd MMM yyyy', { locale })}`;
    }
    if (view === '3day') {
      return `${format(currentDate, 'd MMM', { locale })} - ${format(addDays(currentDate, 2), 'd MMM yyyy', { locale })}`;
    }
    return format(currentDate, 'EEEE d MMMM yyyy', { locale });
  };

  const viewLabels = lang === 'fr'
    ? { month: 'Mois', week: 'Semaine', '3day': '3 jours', day: 'Jour' }
    : { month: 'Month', week: 'Week', '3day': '3 Days', day: 'Day' };

  const handlePromoClick = (promoData) => {
    onFlashPromo(promoData);
    setExpandedPromo(null);
  };

  return (
    <div data-testid="admin-calendar-enhanced">
      {/* Toolbar */}
      <div className="flex flex-col gap-3 mb-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-1 sm:gap-2 min-w-0">
            <Button variant="ghost" size="sm" onClick={() => navigate(-1)} className="text-slate-400 hover:text-white p-1 sm:p-2" data-testid="cal-prev">
              <ChevronLeft className="w-5 h-5" />
            </Button>
            <h3 className="font-heading text-sm sm:text-lg font-bold text-white capitalize text-center truncate" data-testid="cal-title">
              {getTitle()}
            </h3>
            <Button variant="ghost" size="sm" onClick={() => navigate(1)} className="text-slate-400 hover:text-white p-1 sm:p-2" data-testid="cal-next">
              <ChevronRight className="w-5 h-5" />
            </Button>
            <Button variant="outline" size="sm" onClick={goToToday} className="ml-1 border-[#2A2A2A] text-slate-300 hover:border-[#00FF00] hover:text-[#00FF00] text-[10px] sm:text-xs h-7 sm:h-8 px-2 sm:px-3" data-testid="cal-today">
              {lang === 'fr' ? "Auj." : 'Today'}
            </Button>
          </div>
          <div className="flex bg-[#1A1A1A] rounded-lg border border-[#2A2A2A] p-0.5">
            {['day', '3day', 'week', 'month'].map(v => (
              <button key={v} onClick={() => { setView(v); setSelectedDay(null); setExpandedPromo(null); }}
                className={`px-2 sm:px-3 py-1 sm:py-1.5 rounded-md text-[10px] sm:text-xs font-semibold transition-all ${view === v ? 'bg-[#00FF00] text-black' : 'text-slate-400 hover:text-white'}`}
                data-testid={`cal-view-${v}`}
              >{viewLabels[v]}</button>
            ))}
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button onClick={() => openBlockModal()} className="bg-red-500/90 text-white hover:bg-red-600 font-bold text-[10px] sm:text-xs h-8 sm:h-9 px-3 sm:px-4" data-testid="cal-block-btn">
            <Ban className="w-3.5 h-3.5 mr-1" />
            {lang === 'fr' ? 'Bloquer' : 'Block'}
          </Button>
          <Button onClick={() => onAddBooking()} className="bg-[#00FF00] text-black hover:bg-[#00CC00] font-bold text-[10px] sm:text-xs h-8 sm:h-9 px-3 sm:px-4" data-testid="cal-new-booking-btn">
            <UserPlus className="w-3.5 h-3.5 mr-1" />
            {lang === 'fr' ? 'Réservation' : 'New booking'}
          </Button>
          {isSuperAdmin && sites.length > 1 && (
            <Select value={siteFilter} onValueChange={setSiteFilter}>
              <SelectTrigger className="w-32 sm:w-48 bg-[#1A1A1A] border-[#2A2A2A] text-white text-[10px] sm:text-xs h-8 sm:h-9" data-testid="cal-site-filter">
                <MapPin className="w-3 h-3 mr-1 text-[#00FF00]" /><SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[#1A1A1A] border-[#2A2A2A]">
                <SelectItem value="all" className="text-white">{lang === 'fr' ? 'Tous les sites' : 'All sites'}</SelectItem>
                {sites.map(s => <SelectItem key={s.id} value={s.id} className="text-white">{s.city}</SelectItem>)}
              </SelectContent>
            </Select>
          )}
        </div>
      </div>

      {view === 'month' && <MonthView currentDate={currentDate} calData={calData} lang={lang} selectedDay={selectedDay} setSelectedDay={setSelectedDay} onAddBooking={onAddBooking} getBookingsForDay={getBookingsForDay} locale={locale} isDayBlocked={isDayBlocked} getBlocksForDay={getBlocksForDay} openBlockModal={openBlockModal} handleUnblock={handleUnblock} onBookingClick={setSelectedBookingId} />}
      {(view === 'week' || view === '3day') && <GridView view={view} currentDate={currentDate} getBookingsForDay={getBookingsForDay} getPromoForSlot={getPromoForSlot} getActivePromos={getActivePromos} onFlashPromo={handlePromoClick} expandedPromo={expandedPromo} setExpandedPromo={setExpandedPromo} lang={lang} locale={locale} isSlotBlocked={isSlotBlocked} isDayBlocked={isDayBlocked} onBookingClick={setSelectedBookingId} onAddBooking={onAddBooking} />}
      {view === 'day' && <DayView currentDate={currentDate} getBookingsForDay={getBookingsForDay} getPromoForSlot={getPromoForSlot} getActivePromos={getActivePromos} onFlashPromo={handlePromoClick} expandedPromo={expandedPromo} setExpandedPromo={setExpandedPromo} onAddBooking={onAddBooking} lang={lang} locale={locale} isSlotBlocked={isSlotBlocked} getBlocksForDay={getBlocksForDay} handleUnblock={handleUnblock} openBlockModal={openBlockModal} onBookingClick={setSelectedBookingId} />}

      {/* Booking Detail Modal */}
      <BookingDetailModal
        bookingId={selectedBookingId}
        open={!!selectedBookingId}
        onClose={() => { setSelectedBookingId(null); loadCalendar(); }}
        onConfirmPayment={async (id, data) => {
          await adminAPI.confirmOnsitePayment(id, data);
          toast.success('Paiement confirmé');
          loadCalendar();
        }}
        API_BASE={API_BASE}
        activities={activities}
        onRefresh={loadCalendar}
      />

      {/* Block Modal */}
      <Dialog open={blockModal} onOpenChange={setBlockModal}>
        <DialogContent className="bg-[#1A1A1A] border-[#2A2A2A] text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl flex items-center gap-2">
              <Ban className="w-5 h-5 text-red-400" /> Bloquer des créneaux
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div>
              <Label className="text-xs text-slate-400">Date</Label>
              <Input type="date" value={blockDate} onChange={e => {
                setBlockDate(e.target.value);
                const dayData = calData.find(c => c.date === e.target.value);
                setBlockConflicts(dayData?.bookings || []);
              }} className="bg-[#0A0A0A] border-[#2A2A2A] text-white mt-1" data-testid="block-modal-date" />
            </div>

            {blockConflicts.length > 0 && (
              <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-xl p-3" data-testid="block-conflict-warning">
                <div className="flex items-center gap-2 text-yellow-400 text-sm font-medium mb-2">
                  <AlertTriangle className="w-4 h-4" /> {blockConflicts.length} réservation(s) existante(s)
                </div>
                <div className="space-y-1 max-h-32 overflow-y-auto">
                  {blockConflicts.map(b => (
                    <div key={b.id} className="text-xs text-yellow-300/80 flex justify-between">
                      <span>{b.time_slot} - {b.activity_name}</span>
                      <span>{b.client_name}</span>
                    </div>
                  ))}
                </div>
                <p className="text-[10px] text-yellow-400/60 mt-2">Les réservations existantes ne seront pas annulées automatiquement.</p>
              </div>
            )}

            <div>
              <Label className="text-xs text-slate-400">Activité</Label>
              <Select value={blockActivityId || 'all'} onValueChange={v => setBlockActivityId(v === 'all' ? '' : v)}>
                <SelectTrigger className="bg-[#0A0A0A] border-[#2A2A2A] text-white mt-1" data-testid="block-modal-activity">
                  <SelectValue placeholder="Toutes les activités" />
                </SelectTrigger>
                <SelectContent className="bg-[#1A1A1A] border-[#2A2A2A]">
                  <SelectItem value="all">Toutes les activités</SelectItem>
                  {activities.filter(a => !effectiveSiteId || a.site_id === effectiveSiteId).map(a => (
                    <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="text-xs text-slate-400 mb-2 block">Type de blocage</Label>
              <div className="flex gap-3">
                <Button size="sm" variant={blockMode === 'day' ? 'default' : 'outline'}
                  onClick={() => { setBlockMode('day'); setBlockTimeSlots([]); }}
                  className={blockMode === 'day' ? 'bg-red-500 text-white hover:bg-red-600' : 'border-[#2A2A2A] text-slate-400'}
                  data-testid="block-modal-mode-day">
                  <Calendar className="w-3 h-3 mr-1" /> Journée entière
                </Button>
                <Button size="sm" variant={blockMode === 'slots' ? 'default' : 'outline'}
                  onClick={() => setBlockMode('slots')}
                  className={blockMode === 'slots' ? 'bg-red-500 text-white hover:bg-red-600' : 'border-[#2A2A2A] text-slate-400'}
                  data-testid="block-modal-mode-slots">
                  <Clock className="w-3 h-3 mr-1" /> Créneaux spécifiques
                </Button>
              </div>
            </div>

            {blockMode === 'slots' && (
              <div className="flex flex-wrap gap-1.5 max-h-40 overflow-y-auto">
                {TIME_SLOTS.map(slot => (
                  <button key={slot} onClick={() => toggleBlockSlot(slot)}
                    className={`px-2.5 py-1 rounded text-xs font-medium transition-all ${
                      blockTimeSlots.includes(slot)
                        ? 'bg-red-500/20 text-red-400 border border-red-500/40'
                        : 'bg-[#0A0A0A] text-slate-400 border border-[#2A2A2A] hover:border-red-500/30'
                    }`} data-testid={`block-modal-slot-${slot}`}>
                    {slot}
                  </button>
                ))}
              </div>
            )}

            <div>
              <Label className="text-xs text-slate-400">Raison (optionnel)</Label>
              <Input value={blockReason} onChange={e => setBlockReason(e.target.value)} placeholder="Absence, fermeture..."
                className="bg-[#0A0A0A] border-[#2A2A2A] text-white mt-1" data-testid="block-modal-reason" />
            </div>

            <div className="flex gap-3 pt-2">
              <Button onClick={handleBlock} disabled={blockSaving || !blockDate}
                className="flex-1 bg-red-500 text-white hover:bg-red-600 font-bold" data-testid="block-modal-confirm">
                {blockSaving ? '...' : <><Ban className="w-4 h-4 mr-2" /> Bloquer</>}
              </Button>
              <Button onClick={() => setBlockModal(false)} variant="outline"
                className="border-[#2A2A2A] text-slate-400 hover:bg-[#2A2A2A]">Annuler</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ─── MONTH VIEW ─── */
function MonthView({ currentDate, selectedDay, setSelectedDay, onAddBooking, getBookingsForDay, lang, locale, isDayBlocked, getBlocksForDay, openBlockModal, handleUnblock, onBookingClick }) {
  const calDays = eachDayOfInterval({ start: startOfMonth(currentDate), end: endOfMonth(currentDate) });
  const firstDayOffset = (getDay(startOfMonth(currentDate)) + 6) % 7;
  return (
    <>
      <div className="bg-[#1A1A1A] rounded-2xl border border-[#2A2A2A] p-4" data-testid="cal-month-grid">
        <div className="grid grid-cols-7 gap-1 mb-2">
          {(lang === 'fr' ? ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'] : ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']).map(d => (
            <div key={d} className="text-center text-xs font-semibold text-slate-500 py-2">{d}</div>
          ))}
        </div>
        <div className="grid grid-cols-7 gap-1">
          {Array(firstDayOffset).fill(null).map((_, i) => <div key={`e-${i}`} />)}
          {calDays.map(d => {
            const dayData = getBookingsForDay(d);
            const today = dfIsToday(d);
            const isSelected = selectedDay && isSameDay(d, selectedDay);
            const dateStr = format(d, 'yyyy-MM-dd');
            const blocked = isDayBlocked(dateStr);
            const dayBlocks = getBlocksForDay(dateStr);
            return (
              <button key={d.toISOString()} onClick={() => setSelectedDay(isSelected ? null : d)}
                className={`p-2 rounded-lg text-center min-h-[70px] transition-all relative ${
                  blocked ? 'bg-red-500/10 border border-red-500/30' :
                  isSelected ? 'bg-[#00FF00]/20 border border-[#00FF00]' :
                  today ? 'bg-[#2A2A2A] border border-[#333]' : 'hover:bg-[#222] border border-transparent'
                }`}>
                <span className={`text-sm ${blocked ? 'text-red-400 font-bold' : today ? 'text-[#00FF00] font-bold' : 'text-slate-400'}`}>{format(d, 'd')}</span>
                {blocked && <div className="mt-0.5"><Badge className="bg-red-500/20 text-red-400 border-0 text-[9px] px-1">Fermé</Badge></div>}
                {dayData && !blocked && <div className="mt-1"><Badge className="bg-[#00FF00]/20 text-[#00FF00] border-0 text-[10px] px-1.5">{dayData.count}</Badge></div>}
                {!blocked && dayBlocks.length > 0 && <div className="absolute top-1 right-1 w-2 h-2 rounded-full bg-red-400" title="Blocage partiel" />}
              </button>
            );
          })}
        </div>
      </div>
      {selectedDay && <DayDetail day={selectedDay} dayData={getBookingsForDay(selectedDay)} onAddBooking={onAddBooking} lang={lang} locale={locale} blocks={getBlocksForDay(format(selectedDay, 'yyyy-MM-dd'))} openBlockModal={openBlockModal} handleUnblock={handleUnblock} onBookingClick={onBookingClick} />}
    </>
  );
}

/* ─── GRID VIEW (Week / 3-Day) ─── */
function GridView({ view, currentDate, getBookingsForDay, getPromoForSlot, getActivePromos, onFlashPromo, expandedPromo, setExpandedPromo, lang, locale, isSlotBlocked, isDayBlocked, onBookingClick, onAddBooking }) {
  const days = view === 'week'
    ? eachDayOfInterval({ start: startOfWeek(currentDate, { weekStartsOn: 1 }), end: endOfWeek(currentDate, { weekStartsOn: 1 }) })
    : eachDayOfInterval({ start: currentDate, end: addDays(currentDate, 2) });

  return (
    <div className="bg-[#1A1A1A] rounded-2xl border border-[#2A2A2A] overflow-hidden" data-testid="cal-grid-view">
      <div className="overflow-x-auto">
        <div className="min-w-[700px]">
          {/* Header */}
          <div className="grid border-b border-[#2A2A2A]" style={{ gridTemplateColumns: `60px repeat(${days.length}, 1fr)` }}>
            <div className="p-2 text-xs text-slate-600 border-r border-[#2A2A2A]" />
            {days.map(d => {
              const today = dfIsToday(d);
              const dateStr = format(d, 'yyyy-MM-dd');
              const blocked = isDayBlocked(dateStr);
              return (
                <div key={d.toISOString()} className={`p-3 text-center border-r border-[#2A2A2A] last:border-r-0 ${blocked ? 'bg-red-500/5' : today ? 'bg-[#00FF00]/5' : ''}`}>
                  <div className={`text-xs font-semibold ${blocked ? 'text-red-400' : today ? 'text-[#00FF00]' : 'text-slate-500'}`}>{format(d, 'EEE', { locale })}</div>
                  <div className={`text-lg font-bold ${blocked ? 'text-red-400' : today ? 'text-[#00FF00]' : 'text-white'}`}>{format(d, 'd')}</div>
                  {blocked && <span className="text-[9px] text-red-400 font-bold">FERMÉ</span>}
                </div>
              );
            })}
          </div>

          {/* Time grid */}
          {TIME_SLOTS.map(time => (
            <div key={time} className="grid border-b border-[#2A2A2A]/50 last:border-b-0" style={{ gridTemplateColumns: `60px repeat(${days.length}, 1fr)` }}>
              <div className="p-1.5 text-[11px] text-slate-600 font-mono border-r border-[#2A2A2A] text-right pr-2 flex items-start justify-end">
                {time.endsWith(':00') ? time : ''}
              </div>
              {days.map(d => {
                const dateStr = format(d, 'yyyy-MM-dd');
                const dayData = getBookingsForDay(d);
                const bookings = dayData?.bookings?.filter(b => b.time_slot === time) || [];
                const bookedActivityIds = [...new Set(bookings.map(b => b.activity_id))];
                const promos = bookings.length > 0 ? getPromoForSlot(dateStr, time, bookedActivityIds) : [];
                const activePromos = getActivePromos(dateStr, time);
                const today = dfIsToday(d);
                const promoKey = `${dateStr}-${time}`;
                const isExpanded = expandedPromo === promoKey;
                const slotIsBlocked = isSlotBlocked(dateStr, time);
                // Check if this slot is covered by a longer booking from a previous slot
                const coveredByPrev = dayData?.bookings?.some(b => {
                  if (b.time_slot === time) return false;
                  const [bh, bm] = b.time_slot.split(':').map(Number);
                  const [th, tm] = time.split(':').map(Number);
                  const bStart = bh * 60 + bm;
                  const tStart = th * 60 + tm;
                  return tStart > bStart && tStart < bStart + (b.duration_minutes || 30);
                }) || false;

                return (
                  <div key={`${d.toISOString()}-${time}`}
                    className={`min-h-[32px] border-r border-[#2A2A2A]/30 last:border-r-0 p-0.5 relative overflow-visible ${
                      slotIsBlocked ? 'bg-red-500/[0.06]' : coveredByPrev ? '' : today ? 'bg-[#00FF00]/[0.02]' : ''
                    } ${time.endsWith(':00') ? 'border-t border-[#2A2A2A]/30' : ''}`}>
                    {coveredByPrev && bookings.length === 0 && (
                      <div className="w-full h-full" />
                    )}
                    {!coveredByPrev && slotIsBlocked && bookings.length === 0 && (
                      <div className="flex items-center justify-center h-full opacity-30">
                        <Ban className="w-3 h-3 text-red-400" />
                      </div>
                    )}
                    {!coveredByPrev && !slotIsBlocked && bookings.length === 0 && promos.length === 0 && (
                      <button
                        onClick={() => onAddBooking(d, time)}
                        className="w-full h-full min-h-[28px] rounded hover:bg-[#00FF00]/5 hover:border-[#00FF00]/20 border border-transparent transition-all flex items-center justify-center group"
                        data-testid={`cal-empty-slot-${dateStr}-${time}`}
                      >
                        <Plus className="w-3 h-3 text-[#00FF00] opacity-0 group-hover:opacity-40 transition-opacity" />
                      </button>
                    )}
                    {bookings.map(b => {
                      const hasPromo = activePromos.some(p => p.activity_id === b.activity_id);
                      const spans = Math.max(1, Math.round((b.duration_minutes || 30) / 30));
                      return (
                        <div key={b.id} onClick={(e) => { e.stopPropagation(); onBookingClick(b.id); }}
                          className={`${hasPromo ? 'bg-yellow-500/10 border-l-2 border-yellow-400' : b.status === 'cancelled' ? 'bg-red-500/5 border-l-2 border-red-500/50' : b.status === 'pending_onsite' ? 'bg-amber-500/10 border-l-2 border-amber-400' : 'bg-[#00FF00]/10 border-l-2 border-[#00FF00]'} rounded-r px-1.5 py-1 mb-0.5 cursor-pointer hover:brightness-125 transition-all ${spans > 1 ? 'relative z-10' : ''}`}
                          style={spans > 1 ? { minHeight: `${spans * 32 - 4}px` } : undefined}
                          title={`${b.client_name} - ${b.activity_name} - ${b.duration_minutes}min - ${b.price}€${hasPromo ? ' (PROMO ACTIVE)' : ''}`}
                          data-testid={`cal-booking-${b.id}`}>
                          <div className="flex items-center gap-1">
                            <span className={`text-[10px] font-semibold ${hasPromo ? 'text-yellow-400' : b.status === 'pending_onsite' ? 'text-amber-400' : 'text-[#00FF00]'} truncate`}>{b.activity_name}</span>
                            {hasPromo && <Zap className="w-2.5 h-2.5 text-yellow-400 flex-shrink-0" />}
                          </div>
                          <span className="text-[9px] text-slate-400 truncate block">{b.client_name} · {b.duration_minutes}min · <span className="text-white font-semibold">{b.quantity || 1}p</span></span>
                          {spans > 1 && <span className="text-[9px] text-slate-500 block mt-0.5">{b.price}€ {b.status === 'pending_onsite' ? '· A payer' : b.payment_detail_method ? `· ${b.payment_detail_method === 'cb' ? 'CB' : b.payment_detail_method === 'especes' ? 'Esp.' : b.payment_detail_method}` : ''}</span>}
                        </div>
                      );
                    })}
                    {promos.length > 0 && !isExpanded && (
                      <button onClick={() => setExpandedPromo(promoKey)}
                        className="w-full flex items-center justify-center gap-1 rounded bg-yellow-500/5 hover:bg-yellow-500/15 border border-dashed border-yellow-500/30 py-0.5 mt-0.5 transition-colors"
                        data-testid={`cal-flash-${promoKey}`}>
                        <Zap className="w-3 h-3 text-yellow-400" />
                        <span className="text-[9px] text-yellow-400 font-semibold">{promos.reduce((s, p) => s + p.slot.remaining, 0)} pl.</span>
                      </button>
                    )}
                    {isExpanded && (
                      <div className="absolute z-20 top-0 left-0 w-56 bg-[#111] border border-yellow-500/30 rounded-lg shadow-xl p-2 space-y-1" data-testid={`cal-flash-picker-${promoKey}`}>
                        <div className="flex justify-between items-center mb-1">
                          <span className="text-[10px] text-yellow-400 font-bold flex items-center gap-1"><Zap className="w-3 h-3" /> {time}</span>
                          <button onClick={() => setExpandedPromo(null)} className="text-slate-500 hover:text-white"><X className="w-3 h-3" /></button>
                        </div>
                        {promos.map(ps => (
                          <button key={ps.activity_id} onClick={() => onFlashPromo({
                            site_id: ps.site_id, activity_id: ps.activity_id,
                            activity_name: ps.activity_name, date: ps.date,
                            time_slot: time, duration_minutes: ps.duration_minutes,
                            price: ps.price, label: ps.label,
                            remaining: ps.slot.remaining, max_capacity: ps.slot.max_capacity
                          })} className="w-full flex items-center justify-between bg-[#0A0A0A] hover:bg-yellow-500/10 border border-[#2A2A2A] hover:border-yellow-500/40 rounded-md px-2 py-1.5 transition-all"
                            data-testid={`cal-flash-pick-${ps.activity_id}`}>
                            <span className="text-[10px] text-white font-medium truncate">{ps.activity_name}</span>
                            <span className="text-[9px] text-yellow-400 ml-1 flex-shrink-0">{ps.slot.remaining} pl.</span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ─── DAY VIEW ─── */
function DayView({ currentDate, getBookingsForDay, lang, onAddBooking, getPromoForSlot, getActivePromos, onFlashPromo, expandedPromo, setExpandedPromo, locale, isSlotBlocked, getBlocksForDay, handleUnblock, openBlockModal, onBookingClick }) {
  const dayData = getBookingsForDay(currentDate);
  const today = dfIsToday(currentDate);
  const dateStr = format(currentDate, 'yyyy-MM-dd');
  const blocks = getBlocksForDay(dateStr);

  return (
    <div className="bg-[#1A1A1A] rounded-2xl border border-[#2A2A2A] overflow-hidden" data-testid="cal-day-view">
      <div className={`p-4 border-b border-[#2A2A2A] flex justify-between items-center ${today ? 'bg-[#00FF00]/5' : ''}`}>
        <div>
          <h4 className={`font-heading text-lg font-bold capitalize ${today ? 'text-[#00FF00]' : 'text-white'}`}>
            {format(currentDate, 'EEEE d MMMM yyyy', { locale })}
          </h4>
          <p className="text-xs text-slate-500 mt-0.5">
            {dayData ? `${dayData.count} ${lang === 'fr' ? 'réservation(s)' : 'booking(s)'}` : (lang === 'fr' ? 'Aucune réservation' : 'No bookings')}
            {blocks.length > 0 && <span className="text-red-400 ml-2">· {blocks.length} blocage(s)</span>}
          </p>
        </div>
        <div className="flex gap-2">
          <Button onClick={() => openBlockModal(currentDate)} variant="outline" className="border-red-500/30 text-red-400 hover:bg-red-500/10 font-bold text-xs h-8" data-testid="cal-day-block-btn">
            <Ban className="w-3.5 h-3.5 mr-1" /> Bloquer
          </Button>
          <Button onClick={() => onAddBooking(currentDate)} className="bg-[#00FF00] text-black hover:bg-[#00CC00] font-bold text-xs h-8" data-testid="cal-day-add-booking">
            <Plus className="w-3.5 h-3.5 mr-1" /> {lang === 'fr' ? 'Réservation' : 'Booking'}
          </Button>
        </div>
      </div>

      {/* Show active blocks */}
      {blocks.length > 0 && (
        <div className="p-3 bg-red-500/5 border-b border-red-500/20">
          {blocks.map(b => (
            <div key={b.id} className="flex items-center justify-between py-1">
              <div className="flex items-center gap-2 text-xs text-red-400">
                <Ban className="w-3 h-3" />
                <span className="font-medium">
                  {(!b.time_slots || b.time_slots.length === 0) ? 'Journée entière' : `Créneaux: ${b.time_slots.join(', ')}`}
                </span>
                {b.reason && <span className="text-red-400/50">— {b.reason}</span>}
              </div>
              <button onClick={() => handleUnblock(b.id)} className="text-red-400/60 hover:text-red-300 transition-colors" data-testid={`cal-unblock-${b.id}`}>
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          ))}
        </div>
      )}

      <div className="divide-y divide-[#2A2A2A]/30">
        {TIME_SLOTS.filter(t => t.endsWith(':00')).map(time => {
          const halfBookings = dayData?.bookings?.filter(b => b.time_slot === time) || [];
          const halfHourTime = time.replace(':00', ':30');
          const halfHourBookings = dayData?.bookings?.filter(b => b.time_slot === halfHourTime) || [];
          const hasBookings = halfBookings.length > 0 || halfHourBookings.length > 0;

          // Get booked activity IDs for this hour
          const bookedActivityIds = [...new Set([
            ...halfBookings.map(b => b.activity_id),
            ...halfHourBookings.map(b => b.activity_id)
          ])];

          // Promos only for activities that ALREADY have bookings AND still have remaining capacity
          const promosHour = hasBookings ? getPromoForSlot(dateStr, time, bookedActivityIds) : [];
          const promosHalf = hasBookings ? getPromoForSlot(dateStr, halfHourTime, bookedActivityIds) : [];
          const allPromos = [...promosHour];
          for (const p of promosHalf) {
            if (!allPromos.find(a => a.activity_id === p.activity_id)) allPromos.push(p);
          }
          const promoKey = `${dateStr}-${time}`;
          const isExpanded = expandedPromo === promoKey;

          return (
            <div key={time} className="grid grid-cols-[70px_1fr] min-h-[60px]">
              <div className="p-2 text-sm text-slate-500 font-mono border-r border-[#2A2A2A] flex items-start justify-end pr-3 pt-3">{time}</div>
              <div className="p-2 relative">
                {halfBookings.map(b => {
                  const activePromosForSlot = getActivePromos(dateStr, time);
                  const hasPromo = activePromosForSlot.some(p => p.activity_id === b.activity_id);
                  return <BookingCard key={b.id} booking={b} hasPromo={hasPromo} onClick={() => onBookingClick(b.id)} />;
                })}
                {halfHourBookings.map(b => {
                  const activePromosForSlot = getActivePromos(dateStr, halfHourTime);
                  const hasPromo = activePromosForSlot.some(p => p.activity_id === b.activity_id);
                  return <BookingCard key={b.id} booking={b} showTime hasPromo={hasPromo} onClick={() => onBookingClick(b.id)} />;
                })}

                {hasBookings && allPromos.length > 0 && !isExpanded && (
                  <button onClick={() => setExpandedPromo(promoKey)}
                    className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-yellow-500/5 border border-dashed border-yellow-500/25 hover:border-yellow-400/50 hover:bg-yellow-500/10 transition-all w-full mt-1"
                    data-testid={`cal-flash-${promoKey}`}>
                    <Zap className="w-3.5 h-3.5 text-yellow-400" />
                    <span className="text-xs text-yellow-400 font-semibold">
                      {allPromos.reduce((s, p) => s + p.slot.remaining, 0)} {lang === 'fr' ? 'places restantes — Promo flash ?' : 'spots left — Flash promo?'}
                    </span>
                  </button>
                )}

                {isExpanded && (
                  <div className="bg-[#111] border border-yellow-500/30 rounded-xl p-3 space-y-1.5 mt-1" data-testid={`cal-flash-picker-${promoKey}`}>
                    <div className="flex justify-between items-center mb-1">
                      <span className="text-xs text-yellow-400 font-bold flex items-center gap-1.5">
                        <Zap className="w-3.5 h-3.5" /> {lang === 'fr' ? 'Envoyer une promo flash' : 'Send flash promo'} — {time}
                      </span>
                      <button onClick={() => setExpandedPromo(null)} className="text-slate-500 hover:text-white"><X className="w-3.5 h-3.5" /></button>
                    </div>
                    {allPromos.map(ps => (
                      <button key={ps.activity_id}
                        onClick={() => onFlashPromo({
                          site_id: ps.site_id, activity_id: ps.activity_id,
                          activity_name: ps.activity_name, date: ps.date,
                          time_slot: ps.slot.time_slot, duration_minutes: ps.duration_minutes,
                          price: ps.price, label: ps.label,
                          remaining: ps.slot.remaining, max_capacity: ps.slot.max_capacity
                        })}
                        className="w-full flex items-center justify-between bg-[#0A0A0A] hover:bg-yellow-500/10 border border-[#2A2A2A] hover:border-yellow-500/40 rounded-lg px-3 py-2 transition-all group"
                        data-testid={`cal-flash-pick-${ps.activity_id}`}>
                        <div className="text-left">
                          <span className="text-sm text-white font-semibold group-hover:text-yellow-300 block">{ps.activity_name}</span>
                          <span className="text-[10px] text-slate-500">{ps.duration_minutes}min · {ps.price}€</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge className="bg-yellow-500/15 text-yellow-400 border-0 text-[10px]">{ps.slot.remaining} {lang === 'fr' ? 'places' : 'spots'}</Badge>
                          <Zap className="w-3.5 h-3.5 text-yellow-400 opacity-0 group-hover:opacity-100 transition-opacity" />
                        </div>
                      </button>
                    ))}
                  </div>
                )}

                {!hasBookings && (
                  <button
                    onClick={() => onAddBooking(currentDate, time)}
                    className="w-full h-full min-h-[40px] rounded-lg border border-dashed border-transparent hover:border-[#00FF00]/30 hover:bg-[#00FF00]/5 transition-all flex items-center justify-center group"
                    data-testid={`cal-empty-slot-${time}`}
                  >
                    <span className="text-[10px] text-slate-600 opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-1">
                      <Plus className="w-3 h-3" /> Réserver {time}
                    </span>
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ─── DAY DETAIL (month view selected day) ─── */
function DayDetail({ day, dayData, onAddBooking, lang, locale, blocks = [], openBlockModal, handleUnblock, onBookingClick }) {
  return (
    <div className="mt-6 bg-[#1A1A1A] rounded-2xl border border-[#2A2A2A] p-6">
      <div className="flex justify-between items-center mb-4">
        <h4 className="font-heading text-base font-bold text-white capitalize">{format(day, 'EEEE d MMMM yyyy', { locale })}</h4>
        <div className="flex gap-2">
          <Button onClick={() => openBlockModal(day)} variant="outline" className="border-red-500/30 text-red-400 hover:bg-red-500/10 font-bold text-xs h-8" data-testid="cal-detail-block-btn">
            <Ban className="w-3.5 h-3.5 mr-1" /> Bloquer
          </Button>
          <Button onClick={() => onAddBooking(day)} className="bg-[#00FF00] text-black hover:bg-[#00CC00] font-bold text-xs h-8" data-testid="calendar-add-booking">
            <Plus className="w-3.5 h-3.5 mr-1" /> {lang === 'fr' ? 'Réservation' : 'Booking'}
          </Button>
        </div>
      </div>
      {blocks.length > 0 && (
        <div className="mb-4 space-y-2">
          {blocks.map(b => (
            <div key={b.id} className="flex items-center justify-between bg-red-500/10 rounded-lg p-3 border border-red-500/20">
              <div className="flex items-center gap-2 text-sm text-red-400">
                <Ban className="w-4 h-4" />
                <span className="font-medium">
                  {(!b.time_slots || b.time_slots.length === 0) ? 'Journée entière — Fermé' : `Créneaux bloqués: ${b.time_slots.join(', ')}`}
                </span>
                {b.reason && <span className="text-red-400/60">— {b.reason}</span>}
              </div>
              <button onClick={() => handleUnblock(b.id)} className="text-red-400/60 hover:text-red-300"><Trash2 className="w-4 h-4" /></button>
            </div>
          ))}
        </div>
      )}
      {(!dayData || dayData.bookings.length === 0) ? (
        <p className="text-slate-600 text-sm">{lang === 'fr' ? 'Aucune réservation' : 'No bookings'}</p>
      ) : (
        <div className="space-y-2">
          {dayData.bookings.sort((a, b) => a.time_slot.localeCompare(b.time_slot)).map(b => (
            <div key={b.id} onClick={() => onBookingClick(b.id)} className={`flex items-center justify-between rounded-lg p-3 border cursor-pointer hover:border-[#00FF00]/30 transition-colors ${b.status === 'confirmed' && b.payment_detail_method ? 'bg-[#00FF00]/5 border-[#00FF00]/20' : b.status === 'pending_onsite' ? 'bg-amber-500/5 border-amber-500/20' : 'bg-[#0A0A0A] border-[#2A2A2A]'}`} data-testid={`cal-booking-${b.id}`}>
              <div className="flex items-center gap-4">
                <span className="font-mono text-[#00FF00] font-bold text-sm w-12">{b.time_slot}</span>
                <div>
                  <p className="text-white text-sm font-medium">{b.activity_name}</p>
                  <p className="text-xs text-slate-500">{b.client_name} - {b.duration_minutes}min - <span className="text-white font-semibold">{b.quantity || 1} pers.</span></p>
                </div>
              </div>
              <div className="flex items-center gap-2 text-right">
                <span className="text-[#00FF00] font-semibold">{b.price}€</span>
                {b.status === 'confirmed' && b.payment_detail_method ? (
                  <Badge className="bg-[#00FF00]/20 text-[#00FF00] border-0 text-xs font-bold">
                    {b.payment_detail_method === 'cb' ? 'CB' : b.payment_detail_method === 'especes' ? 'Espèces' : b.payment_detail_method === 'cheques_vacances' ? 'Chq.V.' : b.payment_detail_method === 'credits' ? 'Crédits' : b.payment_detail_method === 'lien' ? 'Lien' : b.payment_detail_method === 'carte_cadeau' ? 'Carte cad.' : b.payment_detail_method === 'sport_decouverte' ? 'Sport Déc.' : b.payment_detail_method === 'adrenactive' ? 'Adrénactive' : b.payment_detail_method === 'manawa' ? 'Manawa' : b.payment_detail_method === 'autres' ? (b.payment_detail_other || 'Autres') : 'Payé'}
                  </Badge>
                ) : b.status === 'pending_onsite' ? (
                  <Badge className="bg-amber-500/20 text-amber-400 border-0 text-xs font-bold">A payer</Badge>
                ) : (
                  <Badge className={`${statusColors[b.status] || statusColors.pending} border-0 text-xs`}>{b.status === 'confirmed' ? 'Payé' : b.status}</Badge>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ─── BOOKING CARD ─── */
function BookingCard({ booking: b, showTime, hasPromo, onClick }) {
  const isPaid = b.status === 'confirmed' && b.payment_detail_method;
  const isPendingOnsite = b.status === 'pending_onsite';
  const isCancelled = b.status === 'cancelled';

  const borderColor = isCancelled ? 'border-red-500/50' : isPaid ? 'border-[#00FF00]' : isPendingOnsite ? 'border-amber-400' : hasPromo ? 'border-yellow-400' : 'border-[#00FF00]/50';
  const bgColor = isCancelled ? 'bg-red-500/5' : isPaid ? 'bg-[#00FF00]/10' : isPendingOnsite ? 'bg-amber-500/10' : hasPromo ? 'bg-yellow-500/10' : 'bg-[#00FF00]/5';

  const pmLabels = { cb: 'CB', especes: 'Esp.', cheques_vacances: 'Chq.V.', credits: 'Crédits', lien: 'Lien', carte_cadeau: 'Carte cad.', sport_decouverte: 'Sport Déc.', adrenactive: 'Adrénactive', manawa: 'Manawa', autres: b.payment_detail_other || 'Autres' };

  return (
    <div onClick={onClick} className={`${bgColor} border-l-2 ${borderColor} rounded-r px-3 py-2 mb-1 cursor-pointer hover:brightness-125 transition-all`} data-testid={`cal-booking-${b.id}`}>
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0">
          {showTime && <span className={`text-[11px] font-mono font-bold ${isPendingOnsite ? 'text-amber-400' : isPaid ? 'text-[#00FF00]' : hasPromo ? 'text-yellow-400' : 'text-[#00FF00]'}`}>{b.time_slot}</span>}
          <span className="text-sm font-semibold text-white truncate">{b.activity_name}</span>
          {hasPromo && <span className="flex items-center gap-0.5 bg-yellow-500/20 text-yellow-400 text-[9px] font-bold px-1.5 py-0.5 rounded"><Zap className="w-2.5 h-2.5" />PROMO</span>}
        </div>
        <div className="flex items-center gap-1.5 flex-shrink-0">
          <span className={`text-sm font-bold ${isPaid ? 'text-[#00FF00]' : isPendingOnsite ? 'text-amber-400' : hasPromo ? 'text-yellow-400' : 'text-[#00FF00]'}`}>{b.price}€</span>
          {isPaid && (
            <Badge className="bg-[#00FF00]/20 text-[#00FF00] border-0 text-[9px] font-bold" data-testid={`cal-paid-badge-${b.id}`}>
              {pmLabels[b.payment_detail_method] || 'Payé'}
            </Badge>
          )}
          {isPendingOnsite && <Badge className="bg-amber-500/20 text-amber-400 border-0 text-[9px] font-bold">A payer</Badge>}
          {!isPaid && !isPendingOnsite && <Badge className={`${statusColors[b.status] || statusColors.pending} border-0 text-[10px]`}>{b.status === 'confirmed' ? 'Payé' : b.status}</Badge>}
        </div>
      </div>
      <p className="text-xs text-slate-400 mt-0.5">{b.client_name} · {b.duration_minutes}min · <span className="text-white font-semibold">{b.quantity || 1} pers.</span></p>
    </div>
  );
}
